import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import taskService from '../services/taskService';
import Task from '../models/Task';
import { useAuth } from './AuthContext';

const TaskContext = createContext();

export const useTasks = () => useContext(TaskContext);

export const TaskProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    category: 'all',
    priority: 'all',
    search: '',
    sortBy: 'dueDate',
    sortOrder: 'asc'
  });

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await taskService.getTasks();
      setTasks(response.tasks);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const categoriesData = await taskService.getCategories();
      setCategories(categoriesData);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, []);

  // Load tasks when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchTasks();
      fetchCategories();
    } else {
      // Clear data when not authenticated
      setTasks([]);
      setFilteredTasks([]);
      setCategories([]);
    }
  }, [isAuthenticated, fetchTasks, fetchCategories]);

  // Apply filtering whenever filters or tasks change
  useEffect(() => {
    applyFilters();
  }, [filters, tasks]);

  const getTaskById = async (taskId) => {
    setLoading(true);
    setError(null);
    try {
      return await taskService.getTaskById(taskId);
    } catch (err) {
      console.error(`Error fetching task ${taskId}:`, err);
      setError(`Failed to load task ${taskId}. Please try again.`);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createTask = async (taskData) => {
    setLoading(true);
    setError(null);
    try {
      const newTask = await taskService.createTask(taskData);
      setTasks(prevTasks => [...prevTasks, newTask]);
      return newTask;
    } catch (err) {
      console.error('Error creating task:', err);
      setError('Failed to create task. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateTask = async (taskId, taskData) => {
    setLoading(true);
    setError(null);
    try {
      const updatedTask = await taskService.updateTask(taskId, taskData);
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId ? updatedTask : task
        )
      );
      return updatedTask;
    } catch (err) {
      console.error(`Error updating task ${taskId}:`, err);
      setError('Failed to update task. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteTask = async (taskId) => {
    setLoading(true);
    setError(null);
    try {
      await taskService.deleteTask(taskId);
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskId));
      return true;
    } catch (err) {
      console.error(`Error deleting task ${taskId}:`, err);
      setError('Failed to delete task. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const toggleTaskStatus = async (taskId) => {
    setLoading(true);
    setError(null);
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) throw new Error(`Task with ID ${taskId} not found`);
      
      let updatedTask;
      if (task.isCompleted) {
        updatedTask = await taskService.reopenTask(taskId);
      } else {
        updatedTask = await taskService.completeTask(taskId);
      }
      
      setTasks(prevTasks => 
        prevTasks.map(t => t.id === taskId ? updatedTask : t)
      );
      return updatedTask;
    } catch (err) {
      console.error(`Error toggling task status ${taskId}:`, err);
      setError('Failed to update task status. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async (categoryData) => {
    setLoading(true);
    setError(null);
    try {
      const newCategory = await taskService.createCategory(categoryData);
      setCategories(prevCategories => [...prevCategories, newCategory]);
      return newCategory;
    } catch (err) {
      console.error('Error creating category:', err);
      setError('Failed to create category. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCategory = async (categoryId, categoryData) => {
    setLoading(true);
    setError(null);
    try {
      const updatedCategory = await taskService.updateCategory(categoryId, categoryData);
      setCategories(prevCategories => 
        prevCategories.map(category => 
          category.id === categoryId ? updatedCategory : category
        )
      );
      return updatedCategory;
    } catch (err) {
      console.error(`Error updating category ${categoryId}:`, err);
      setError('Failed to update category. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    setLoading(true);
    setError(null);
    try {
      await taskService.deleteCategory(categoryId);
      setCategories(prevCategories => 
        prevCategories.filter(category => category.id !== categoryId)
      );
      return true;
    } catch (err) {
      console.error(`Error deleting category ${categoryId}:`, err);
      setError('Failed to delete category. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let result = [...tasks];
    
    // Filter by status
    if (filters.status !== 'all') {
      result = result.filter(task => 
        filters.status === 'completed' ? task.isCompleted : !task.isCompleted
      );
    }
    
    // Filter by category
    if (filters.category !== 'all') {
      result = result.filter(task => task.categoryId === filters.category);
    }
    
    // Filter by priority
    if (filters.priority !== 'all') {
      result = result.filter(task => task.priority === filters.priority);
    }
    
    // Filter by search text
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(task => 
        task.title.toLowerCase().includes(searchLower) || 
        task.description.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (filters.sortBy) {
        case 'dueDate':
          // Handle null due dates
          if (!a.dueDate && !b.dueDate) comparison = 0;
          else if (!a.dueDate) comparison = 1;
          else if (!b.dueDate) comparison = -1;
          else comparison = a.dueDate - b.dueDate;
          break;
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'priority':
          const priorities = { high: 3, medium: 2, low: 1 };
          comparison = priorities[b.priority] - priorities[a.priority];
          break;
        case 'createdAt':
          comparison = a.createdAt - b.createdAt;
          break;
        default:
          comparison = 0;
      }
      
      // Apply sort order
      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });
    
    setFilteredTasks(result);
  };

  const updateFilters = (newFilters) => {
    setFilters(prevFilters => ({ ...prevFilters, ...newFilters }));
  };

  const getCategoryById = (categoryId) => {
    return categories.find(category => category.id === categoryId) || null;
  };
  
  const getCategoryColor = (categoryId) => {
    const category = getCategoryById(categoryId);
    return category ? category.color : '#cccccc';
  };

  const value = {
    tasks,
    filteredTasks,
    categories,
    loading,
    error,
    filters,
    fetchTasks,
    getTaskById,
    createTask,
    updateTask,
    deleteTask,
    toggleTaskStatus,
    createCategory,
    updateCategory,
    deleteCategory,
    getCategoryById,
    getCategoryColor,
    updateFilters
  };

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};

export default TaskContext; 